/**
 * ARC-58 — income write guard (pure; no Firestore, no React).
 *
 * THE INCIDENT THIS EXISTS FOR
 * On 2026-08-18 a session's `incomeData` was replaced by the demo scaffold
 * (`getDefaultData().incomeData` — the ascending 5000→100000 salary ladder) and
 * autosave persisted it over real member figures. The mechanism was the load
 * effect: when the Firestore read threw, or returned a document without an
 * `incomeData` field, the catch/else branches seeded that scaffold straight
 * into `incomeData` — the same state `writeUserData` serialises. From there any
 * save wrote demo numbers over the server's real ones.
 *
 * `loadedOk` blocked the write in the failure case, but the defaults were still
 * sitting in the save path, one missed guard away from being written. ARC-58's
 * rule is stronger: demo values never enter the buffer at all.
 *
 * `resolveIncomeLoad` is the single decision point for "what may go on screen,
 * and may it be saved". It is pure so the failure paths can be tested without a
 * Firestore, and it has exactly one way to produce income rows: from the server.
 *
 * This mirrors ARC-14's expense guard, minus the three-way reconcile — income
 * has no server-merge requirement in this issue (see Non-goals).
 */

import { stableStringify } from "../expenses/expenseWriteGuard";

export { stableStringify };

/** The column set a fresh account starts with. */
export const DEFAULT_INCOME_COLUMNS = [
  "salary",
  "business",
  "rental",
  "crypto",
  "affiliate",
];

export const INCOME_LOAD_ERROR =
  "Could not load your income data. Editing is disabled so nothing overwrites what is saved — please refresh the page.";

const asArray = (v) => (Array.isArray(v) ? v : []);

/* Absent means "the member has not saved this yet" — a normal, safe state.
   Anything else that is not an array is a value we do not understand, which is
   NOT the same thing and must not be treated as one. */
const isAbsent = (v) => v === undefined || v === null;
const isUnparseable = (v) => !isAbsent(v) && !Array.isArray(v);

/**
 * Has the member changed the income buffer since the last server-confirmed
 * state? No committed snapshot means nothing has been confirmed yet — treat
 * that as "not dirty" so we never warn about edits the member did not make.
 */
export const hasUnsavedIncomeChanges = (current, committed) => {
  if (!committed) return false;
  const shape = (s) => ({
    columns: asArray(s?.incomeColumns),
    rows: asArray(s?.incomeData),
  });
  return stableStringify(shape(current)) !== stableStringify(shape(committed));
};

/**
 * Decide the income state a load result is allowed to produce.
 *
 * @param {{ok: boolean, data?: object|null}} result
 *   `ok` is false when the read threw or the service reported failure.
 *   `data` is the loaded document, or null/empty for a brand-new account.
 *
 * @returns {{incomeColumns: string[], incomeData: object[],
 *   committed: {incomeColumns: string[], incomeData: object[]}|null,
 *   canSave: boolean, error: string}}
 *   `committed` is the server-confirmed snapshot the tab's widgets render from;
 *   null means nothing is confirmed. `canSave` false means the tab must be
 *   read-only — a failed read tells us nothing about what the server holds, so
 *   writing anything on top of it is guesswork.
 *
 * Note what this function cannot do: there is no branch that returns demo or
 * placeholder rows. `incomeData` is either the server's array or empty.
 */
export const resolveIncomeLoad = ({ ok, data } = {}) => {
  /* One closed state, whatever the reason we do not trust the read — a read
     that failed and a read we could not parse leave us equally ignorant of what
     the server holds, so they must be indistinguishable downstream. */
  const failedRead = () => ({
    incomeColumns: DEFAULT_INCOME_COLUMNS,
    incomeData: [],
    committed: null,
    canSave: false,
    error: INCOME_LOAD_ERROR,
  });

  if (!ok) return failedRead();

  /* Requirement 1 covers a read that returns an "unparseable shape", not only
     one that errors. A present-but-non-array `incomeData` used to be coerced to
     `[]` with saving left ENABLED, so autosave wrote an empty table over a
     server value we had failed to understand — with no user action, ~2s after
     load. Ignoring a value we cannot read is fine; overwriting it is the exact
     data loss this module exists to prevent. Same for the column list. */
  if (isUnparseable(data?.incomeData) || isUnparseable(data?.incomeColumns)) {
    return failedRead();
  }

  const incomeColumns = asArray(data?.incomeColumns).length
    ? asArray(data.incomeColumns)
    : DEFAULT_INCOME_COLUMNS;

  /* A document with no `incomeData` is either a brand-new account or one saved
     before this field existed. Both mean "the member has entered no income
     yet", which is an empty table — not a worked example we then save for them. */
  const incomeData = asArray(data?.incomeData);

  return {
    incomeColumns,
    incomeData,
    committed: { incomeColumns, incomeData },
    canSave: true,
    error: "",
  };
};

export default resolveIncomeLoad;
